// FirmwareUpgrade.js - 固件升级主组件

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import SystemUpdateIcon from '@material-ui/icons/SystemUpdate';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import InfoIcon from '@material-ui/icons/Info';
import GetAppIcon from '@material-ui/icons/GetApp';
import DFUInstructions from './DFUInstructions';
import {
  validateFirmware,
  enterDFUMode,
  getCurrentFirmwareVersion,
  compareVersions,
  calculateSHA256,
  formatFileSize,
  FIRMWARE_CONFIG,
} from './firmwareUpgradeService';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(3),
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  fileInput: {
    display: 'none',
  },
  versionChip: {
    marginLeft: theme.spacing(1),
    fontWeight: 600,
  },
  warningBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  infoBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  downloadLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

export default function FirmwareUpgrade() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const device = useSelector(state => state.device);
  const firmwareVersion = useSelector(state => state.firmwareVersion);
  const { enqueueSnackbar } = useSnackbar();

  // 状态管理
  const [activeStep, setActiveStep] = useState(0);
  const [firmwareFile, setFirmwareFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [sha256Hash, setSha256Hash] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [showDFUDialog, setShowDFUDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [understood, setUnderstood] = useState(false);

  const steps = ['Select Firmware', 'Verify Firmware', 'Confirm Upgrade', 'Enter DFU Mode', 'Flash Firmware'];

  // 获取当前固件版本
  useEffect(() => {
    if (device && !currentVersion) {
      loadCurrentVersion();
    }
  }, [device]);

  const loadCurrentVersion = async () => {
    const result = await getCurrentFirmwareVersion(dispatch);
    if (result.success) {
      setCurrentVersion(result.version);
    } else {
      // 如果无法获取版本，使用 Redux 中的版本
      if (firmwareVersion) {
        setCurrentVersion(firmwareVersion);
      }
    }
  };

  // 处理文件选择
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFirmwareFile(file);
    setValidationResult(null);
    setSha256Hash(null);
    setActiveStep(0);

    // 验证固件
    enqueueSnackbar('Validating firmware file...', { variant: 'info' });
    const result = await validateFirmware(file);
    setValidationResult(result);

    if (result.valid) {
      enqueueSnackbar('Firmware file validated successfully', { variant: 'success' });
      setActiveStep(1);

      // 计算 SHA256
      setCalculating(true);
      const hash = await calculateSHA256(file);
      setSha256Hash(hash);
      setCalculating(false);
    } else {
      enqueueSnackbar('Firmware validation failed', { variant: 'error' });
      result.errors.forEach(err => {
        enqueueSnackbar(err, { variant: 'error' });
      });
    }
  };

  // 处理确认升级
  const handleConfirmUpgrade = () => {
    setShowConfirmDialog(true);
  };

  // 处理开始升级
  const handleStartUpgrade = async () => {
    if (confirmText !== 'UPGRADE' || !understood) {
      enqueueSnackbar('Please confirm all requirements', { variant: 'warning' });
      return;
    }

    setShowConfirmDialog(false);
    setUpgrading(true);
    setActiveStep(3);

    // 发送 APDU 进入 DFU 模式
    enqueueSnackbar('Sending command to enter DFU mode...', { variant: 'info' });

    const result = await enterDFUMode(dispatch);

    if (result.success) {
      enqueueSnackbar('Device is entering DFU mode', { variant: 'success' });
      setActiveStep(4);

      // 等待一下让设备重启
      setTimeout(() => {
        setUpgrading(false);
        setShowDFUDialog(true);
      }, 2000);
    } else {
      enqueueSnackbar(`Failed to enter DFU mode: ${result.error}`, { variant: 'error' });
      setUpgrading(false);
      setActiveStep(2);
    }
  };

  // 渲染步骤内容
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body1" paragraph>
              Select a firmware file (.bin) to upload. You can download the latest firmware from our official website.
            </Typography>

            <Button
              variant="outlined"
              color="primary"
              href={FIRMWARE_CONFIG.OFFICIAL_DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<GetAppIcon />}
              style={{ marginBottom: 16 }}
            >
              Download Latest Firmware
            </Button>

            <Box>
              <input
                accept=".bin"
                className={classes.fileInput}
                id="firmware-file-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="firmware-file-input">
                <Button
                  variant="contained"
                  color="primary"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={!device}
                >
                  Select Firmware File
                </Button>
              </label>
            </Box>

            {!device && (
              <Alert severity="warning" style={{ marginTop: 16 }}>
                Please connect your device first
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            {validationResult && validationResult.valid && (
              <>
                <Alert severity="success" style={{ marginBottom: 16 }}>
                  <AlertTitle>✅ Firmware Validated Successfully</AlertTitle>
                  <Typography variant="body2">
                    The firmware file has passed all validation checks.
                  </Typography>
                </Alert>

                {/* 固件信息 */}
                <Paper className={classes.infoBox}>
                  <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                    Firmware Information:
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="body2">
                      <strong>File Name:</strong> {validationResult.fileName}
                    </Typography>
                    <Typography variant="body2">
                      <strong>File Size:</strong> {formatFileSize(validationResult.fileSize)}
                    </Typography>
                    {validationResult.detectedVersion && (
                      <Typography variant="body2">
                        <strong>Detected Version:</strong> {validationResult.detectedVersion}
                      </Typography>
                    )}
                    {sha256Hash && (
                      <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
                        <strong>SHA256:</strong> {sha256Hash}
                      </Typography>
                    )}
                    {calculating && (
                      <Typography variant="body2" color="textSecondary">
                        Calculating SHA256...
                      </Typography>
                    )}
                  </Box>
                </Paper>

                {/* 警告信息 */}
                {validationResult.warnings && validationResult.warnings.length > 0 && (
                  <Alert severity="warning" style={{ marginTop: 16 }}>
                    <AlertTitle>Warnings:</AlertTitle>
                    {validationResult.warnings.map((warning, idx) => (
                      <Typography key={idx} variant="body2">
                        • {warning}
                      </Typography>
                    ))}
                  </Alert>
                )}

                {/* 版本比较 */}
                {currentVersion && validationResult.detectedVersion && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                      Version Comparison:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip
                        label={`Current: ${currentVersion}`}
                        color="default"
                        size="small"
                      />
                      <Typography>→</Typography>
                      <Chip
                        label={`New: ${validationResult.detectedVersion}`}
                        color="primary"
                        size="small"
                      />
                    </Box>
                    {(() => {
                      const comparison = compareVersions(currentVersion, validationResult.detectedVersion);
                      if (comparison.isDowngrade) {
                        return (
                          <Alert severity="error" style={{ marginTop: 8 }}>
                            ⚠️ This is a downgrade! Downgrading firmware may cause issues.
                          </Alert>
                        );
                      } else if (comparison.isSame) {
                        return (
                          <Alert severity="info" style={{ marginTop: 8 }}>
                            ℹ️ This is the same version as currently installed.
                          </Alert>
                        );
                      } else if (comparison.isNewer) {
                        return (
                          <Alert severity="success" style={{ marginTop: 8 }}>
                            ✅ This is a newer version.
                          </Alert>
                        );
                      }
                    })()}
                  </Box>
                )}

                <Box mt={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirmUpgrade}
                    startIcon={<SystemUpdateIcon />}
                  >
                    Proceed to Upgrade
                  </Button>
                </Box>
              </>
            )}

            {validationResult && !validationResult.valid && (
              <Alert severity="error">
                <AlertTitle>❌ Validation Failed</AlertTitle>
                {validationResult.errors.map((error, idx) => (
                  <Typography key={idx} variant="body2">
                    • {error}
                  </Typography>
                ))}
              </Alert>
            )}
          </Box>
        );

      case 3:
      case 4:
        return (
          <Box>
            <Alert severity="info" style={{ marginBottom: 16 }}>
              <AlertTitle>Device Entering DFU Mode</AlertTitle>
              <Typography variant="body2">
                The device is restarting into DFU (Device Firmware Update) mode.
                Please wait for the DFU instructions dialog to appear.
              </Typography>
            </Alert>
            {upgrading && <LinearProgress style={{ marginTop: 16 }} />}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Card className={classes.card} data-firmware-upgrade-card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h5">
              Firmware Upgrade
            </Typography>
            {currentVersion && (
              <Chip
                label={`Current: v${currentVersion}`}
                color="primary"
                size="small"
                className={classes.versionChip}
              />
            )}
          </Box>

          {/* 重要提示 */}
          <Alert severity="warning" className={classes.warningBox}>
            <AlertTitle>⚠️ Important Notice</AlertTitle>
            <Typography variant="body2">
              • Backup your data before upgrading firmware
            </Typography>
            <Typography variant="body2">
              • Do not disconnect the device during the upgrade process
            </Typography>
            <Typography variant="body2">
              • Ensure you have dfu-util installed on your system
            </Typography>
            <Typography variant="body2">
              • Download firmware only from the official website
            </Typography>
          </Alert>

          {/* 步骤指示器 */}
          <Stepper activeStep={activeStep} style={{ marginBottom: 24 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* 步骤内容 */}
          {renderStepContent()}

          {/* 固件文件信息 */}
          {firmwareFile && (
            <Box mt={3}>
              <Divider style={{ marginBottom: 16 }} />
              <Typography variant="caption" color="textSecondary">
                Selected File: {firmwareFile.name} ({formatFileSize(firmwareFile.size)})
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 确认对话框 */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <WarningIcon color="error" style={{ marginRight: 8 }} />
            Confirm Firmware Upgrade
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" style={{ marginBottom: 16 }}>
            <AlertTitle>⚠️ Warning</AlertTitle>
            <Typography variant="body2">
              This operation will update your device firmware. Please ensure you have:
            </Typography>
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Backed up all important data</li>
              <li>Downloaded the correct firmware file</li>
              <li>Installed dfu-util on your system</li>
            </ul>
          </Alert>

          <FormControlLabel
            control={
              <Checkbox
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
                color="primary"
              />
            }
            label="I understand the risks and have backed up my data"
            style={{ marginBottom: 16 }}
          />

          <DialogContentText>
            Type <strong>UPGRADE</strong> to confirm:
          </DialogContentText>
          <TextField
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type UPGRADE"
            variant="outlined"
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleStartUpgrade}
            color="secondary"
            variant="contained"
            disabled={confirmText !== 'UPGRADE' || !understood || upgrading}
          >
            Start Upgrade
          </Button>
        </DialogActions>
      </Dialog>

      {/* DFU 指引对话框 */}
      <DFUInstructions
        open={showDFUDialog}
        onClose={() => setShowDFUDialog(false)}
        firmwareFile={firmwareFile}
      />
    </>
  );
}
